// src/utils/psa/psa.constants.js
// Centralized PSA configuration and constants

/**
 * PSA Grading Service Levels
 * Sorted by price (low to high)
 * Service names without prices
 */
export const GRADING_SERVICES = [
  'Trading Card Game Bulk',
  'Value Bulk',
  'Value (1979-Older)',
  'Value (1980-Present)',
  'Value Plus',
  'Value Max',
  'Regular',
  'Express',
  'Super Express',
  'Walk-Through',
  'Premium 1',
  'Premium 2',
  'Premium 3',
  'Premium 5',
  'Premium 10'
];

/**
 * Service level to price mapping
 */
export const GRADING_PRICES = {
  'Trading Card Game Bulk': 18.99,
  'Value Bulk': 21.99,
  'Value (1979-Older)': 27.99,
  'Value (1980-Present)': 27.99,
  'Value Plus': 44.99,
  'Value Max': 59.99,
  'Regular': 74.99,
  'Express': 149.00,
  'Super Express': 299.00,
  'Walk-Through': 599.00,
  'Premium 1': 999.00,
  'Premium 2': 1999.00,
  'Premium 3': 2999.00,
  'Premium 5': 4999.00,
  'Premium 10': 9999.00
};

/**
 * Service level to max insured value mapping
 */
export const MAX_INSURED_VALUES = {
  'Trading Card Game Bulk': 300,
  'Value Bulk': 500,
  'Value (1979-Older)': 500,
  'Value (1980-Present)': 500,
  'Value Plus': 500,
  'Value Max': 1000,
  'Regular': 1500,
  'Express': 2500,
  'Super Express': 5000,
  'Walk-Through': 10000,
  'Premium 1': 25000,
  'Premium 2': 50000,
  'Premium 3': 100000,
  'Premium 5': 250000,
  'Premium 10': 250001
};

/**
 * Service level to turnaround time mapping (in business days)
 * Based on typical PSA turnaround times
 */
export const SERVICE_TURNAROUND_DAYS = {
  'Trading Card Game Bulk': 95,
  'Value Bulk': 95,
  'Value (1979-Older)': 75,
  'Value (1980-Present)': 75,
  'Value Plus': 40,
  'Value Max': 30,
  'Regular': 20,
  'Express': 15,
  'Super Express': 7,
  'Walk-Through': 7,
  'Premium 1': 7,
  'Premium 2': 7,
  'Premium 3': 7,
  'Premium 5': 7,
  'Premium 10': 7
};

export const PSA_GRADES = {
  'PSA 10': 'PSA 10 GEM MT',
  'PSA 9': 'PSA 9 MINT',
  'PSA 8.5': 'PSA 8.5 NM-MT+',
  'PSA 8': 'PSA 8 NM-MT',
  'PSA 7.5': 'PSA 7.5 NM+',
  'PSA 7': 'PSA 7 NM',
  'PSA 6.5': 'PSA 6.5 EX-MT+',
  'PSA 6': 'PSA 6 EX-MT',
  'PSA 5.5': 'PSA 5.5 EX+',
  'PSA 5': 'PSA 5 EX',
  'PSA 4.5': 'PSA 4.5 VG-EX+',
  'PSA 4': 'PSA 4 VG-EX',
  'PSA 3.5': 'PSA 3.5 VG+',
  'PSA 3': 'PSA 3 VG',
  'PSA 2': 'PSA 2 GOOD',
  'PSA 1.5': 'PSA 1.5 FR',
  'PSA 1': 'PSA 1 PR',
  'PSA N0': 'Authentic',
  'PSA AA': 'Authentic Altered',
}

/**
 * Formats a service name with its price for display
 * @param {string} serviceName - Service name
 * @returns {string} Formatted string with price (e.g., "Regular ($74.99)")
 */
export const formatServiceWithPrice = (serviceName) => {
  const price = GRADING_PRICES[serviceName];
  if (price === undefined) return serviceName;
  
  const formattedPrice = price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  // Handle Premium 10 which shows as "$9,999.00+"
  if (serviceName === 'Premium 10') {
    return `${serviceName} ($${formattedPrice}+)`;
  }
  
  return `${serviceName} ($${formattedPrice})`;
};

/**
 * Formats the price for a service level
 * @param {string} serviceName - Service name
 * @returns {string} Formatted price (e.g., "$74.99")
 */
export const formatServicePrice = (serviceName) => {
  const price = GRADING_PRICES[serviceName];
  if (price === undefined) return '';
  
  const formattedPrice = price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  // Handle Premium 10 which shows as "$9,999.00+"
  if (serviceName === 'Premium 10') {
    return `$${formattedPrice}+`;
  }
  
  return `$${formattedPrice}`;
};

/**
 * Formats the max insured value for a service level
 * @param {string} serviceName - Service name
 * @returns {string} Formatted max insured value (e.g., "$1,500")
 */
export const formatMaxInsuredValue = (serviceName) => {
  const maxInsured = MAX_INSURED_VALUES[serviceName];
  if (maxInsured === undefined) return '';
  
  return `$${maxInsured.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/**
 * Array of service names formatted with prices for dropdown display
 */
export const GRADING_SERVICES_WITH_PRICES = GRADING_SERVICES.map(formatServiceWithPrice);

/**
 * PSA Submission Status Values
 * Ordered by workflow progression
 */
export const PSA_STATUSES = {
  DRAFT: 'Draft',
  PSA_DROP_OFF: 'PSA Drop-Off',
  SHIPPED_TO_PSA: 'Shipped to PSA',
  ORDER_ARRIVED: 'Received by PSA',
  GRADING_IN_PROGRESS: 'Grading in Progress',
  COMPLETE: 'Complete'
};

/**
 * Initial status for new submissions
 */
export const INITIAL_STATUS_NEW = PSA_STATUSES.PSA_DROP_OFF;

/**
 * Initial status for already graded cards
 */
export const INITIAL_STATUS_GRADED = PSA_STATUSES.COMPLETE;
